import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Highlight from '@/components/base/Highlight';
import MovieCard from '@/components/base/MovieCard';
import { catalog } from '@/mocks/catalog';
import usePageMeta from '@/hooks/usePageMeta';
import { ratingLabel } from '@/utils/format';

const PAGE_SIZE = 6;
const HOT_KEYWORDS = ['科幻', '古装', '悬疑', '家庭', '青春', '犯罪'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [input, setInput] = useState(query);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [likedIds, setLikedIds] = useState<string[]>([]);
  const [likedOnly, setLikedOnly] = useState(false);

  usePageMeta(
    query ? `“${query}”的搜索结果 - 快活影院` : '影视搜索 - 快活影院',
    query
      ? `快活影院关于“${query}”的影视搜索结果，包含相关电影与电视剧的封面、简介与详细信息。`
      : '在快活影院搜索电影、电视剧与影视资讯。',
    query ? `${query},影视搜索,快活影院` : '影视搜索,快活影院',
  );

  useEffect(() => {
    setInput(query);
    setVisible(PAGE_SIZE);
  }, [query]);

  const results = useMemo(() => {
    const key = query.trim().toLowerCase();
    if (!key) return [];
    return catalog.filter(
      (item) =>
        item.title.toLowerCase().includes(key) ||
        item.genres.some((g) => g.toLowerCase().includes(key)) ||
        item.tags.some((t) => t.toLowerCase().includes(key)) ||
        item.director.toLowerCase().includes(key) ||
        item.cast.some((c) => c.toLowerCase().includes(key)),
    );
  }, [query]);

  const displayResults = useMemo(
    () => (likedOnly ? results.filter((item) => likedIds.includes(item.id)) : results),
    [results, likedOnly, likedIds],
  );

  const list = displayResults.slice(0, visible);
  const hasMore = visible < displayResults.length;

  const submit = (value: string) => {
    const q = value.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  const toggleLike = (id: string) => {
    setLikedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const hotItems = [...catalog].sort((a, b) => b.views - a.views).slice(0, 4);

  return (
    <div className="w-full pb-4">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <nav aria-label="面包屑" className="pt-6 text-sm text-foreground-500">
          <ol className="flex items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600 transition-colors">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li className="text-foreground-900">搜索结果</li>
          </ol>
        </nav>

        <h1 className="mt-4 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          搜索结果
        </h1>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(input);
          }}
          className="mt-4 max-w-2xl"
        >
          <div className="flex items-center gap-2 h-12 px-4 rounded-md border border-background-300 bg-background-50 focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              type="text"
              name="q"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、类型或演员"
              aria-label="搜索影视"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            <button
              type="submit"
              className="px-4 py-1.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              搜索
            </button>
          </div>
        </form>

        {query.trim() ? (
          <>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <p className="text-sm text-foreground-600">
                关键词 <span className="font-semibold text-foreground-950">“{query}”</span> 共找到{' '}
                <span className="font-semibold text-accent-600">{results.length}</span> 个结果
              </p>
              {likedIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setLikedOnly((v) => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-colors whitespace-nowrap ${
                    likedOnly
                      ? 'bg-accent-500 text-foreground-950 font-medium'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  <i className="ri-heart-3-line"></i>
                  仅看已点赞（{likedIds.length}）
                </button>
              )}
            </div>

            {list.length > 0 ? (
              <div className="mt-4 space-y-4">
                {list.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 p-4 rounded-lg border border-background-200 bg-background-50"
                  >
                    <Link to={`/detail/${item.id}`} className="w-24 sm:w-28 shrink-0">
                      <span className="block w-full aspect-[2/3] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.poster}
                          alt={`${item.title}海报`}
                          title={`${item.title} - ${item.genres.join(' ')}`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                    </Link>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <Link to={`/detail/${item.id}`} className="min-w-0">
                          <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950 hover:text-primary-600 transition-colors">
                            <Highlight text={item.title} query={query} />
                          </h2>
                        </Link>
                        <button
                          type="button"
                          onClick={() => toggleLike(item.id)}
                          aria-label="点赞"
                          className={`w-8 h-8 shrink-0 flex items-center justify-center rounded-md transition-colors ${
                            likedIds.includes(item.id)
                              ? 'text-accent-500 bg-accent-100'
                              : 'text-foreground-400 hover:text-accent-500 hover:bg-background-100'
                          }`}
                        >
                          <i className={likedIds.includes(item.id) ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                        </button>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                        <span className="flex items-center gap-1 text-accent-600 font-label font-bold">
                          <i className="ri-star-fill text-[10px]"></i>
                          {ratingLabel(item.rating)}
                        </span>
                        <span>{item.year}</span>
                        <span>{item.region}</span>
                        <span>{item.type === 'movie' ? '电影' : '剧集'}</span>
                      </div>
                      <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-2">
                        <Highlight text={item.summary} query={query} />
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {item.genres.map((genre) => (
                          <Link
                            key={genre}
                            to={`/category/${item.type}?genre=${encodeURIComponent(genre)}`}
                            className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-800 text-xs hover:bg-secondary-200 transition-colors"
                          >
                            {genre}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="pt-2 text-center">
                  {hasMore ? (
                    <button
                      type="button"
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-arrow-down-line"></i>
                      加载更多结果
                    </button>
                  ) : (
                    <p className="text-sm text-foreground-400">— 已显示全部结果 —</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="mt-8 py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading font-semibold text-lg text-foreground-900">
                  没有找到与“{query}”相关的影视作品
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  试试更换关键词，或从下面的热门内容开始浏览。
                </p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 max-w-3xl mx-auto px-4">
                  {hotItems.map((item) => (
                    <MovieCard key={item.id} item={item} badge="热门" />
                  ))}
                </div>
              </div>
            )}

            {likedOnly && displayResults.length === 0 && (
              <p className="mt-4 text-sm text-foreground-500">你还没有点赞任何结果，取消“仅看已点赞”即可查看全部。</p>
            )}
          </>
        ) : (
          <div className="mt-8 py-16 text-center rounded-lg border border-dashed border-background-300 bg-background-100">
            <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-500 flex items-center justify-center">
              <i className="ri-search-line text-2xl"></i>
            </span>
            <h2 className="mt-4 font-heading font-semibold text-lg text-foreground-900">
              输入关键词，开始搜索影视作品
            </h2>
            <p className="mt-2 text-sm text-foreground-500">可以搜索作品名称、类型标签或演职人员。</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
              {HOT_KEYWORDS.map((keyword) => (
                <button
                  key={keyword}
                  type="button"
                  onClick={() => submit(keyword)}
                  className="px-3 py-1.5 rounded-full bg-background-50 border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  # {keyword}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}